import { isEnum, isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { isCustomExternalOptionsProperty } from '../../resolvers/isCustomExternalOptionsProperty';
import { isEmptyValue } from '../../validation/utils/isEmptyValue';
import { isExternalOptionsProperty } from 'reference-resolvers-lite/utils/isExternalOptionsProperty';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { getPipelineStagePropertyName } from '../../input/pipeline/utils/pipelineUtils';
import { jsx as _jsx } from "react/jsx-runtime";
// only place where we filter options and get create a new definition
// given a property definition and a set of rules, returns a new definition with filtered options
// if the rule does not have any dependent options, returns the same definition with no change in reference
export const buildPropertyDefinitionWithFilteredOptions = (propertyDefinition, rules) => {
  const optionValues = [];
  for (const rule of rules) {
    optionValues.push(...rule.dependentOptions);
  }
  if (optionValues.length === 0) {
    return propertyDefinition;
  }
  return Object.assign({}, propertyDefinition, {
    options: propertyDefinition.options.filter(option => optionValues.includes(option.value))
  });
};

// given a parsed property value (as array to handle multienum) and a rule, returns a boolean indicating whether the rule is active or not
export const isRuleActive = (values, rule) => values.includes(rule.controllingField.fieldValue);

// given a parsed property value (as array to handle multienum) and a set of rules, finds out if there is a value with no matching rule
// This is helpful in case of multienum properties.
// If one of the values does not have any rules, we should show all options and ignore rules for all other values
export const someValueHasNoRule = (values, rules) => values.length === 0 || rules.length === 0 || !values.every(value => rules.some(rule => rule.controllingField.fieldValue === value));

// given a parsed property value (as array to handle multienum) and a set of rules, returns a set of active rules
// If one of the values does not have any rules, return no rules
export const getActiveRules = (values, rules) => {
  if (someValueHasNoRule(values, rules)) {
    return [];
  }
  return rules.filter(rule => isRuleActive(values, rule));
};
export const getParsedPropertyValueAsArray = (propertyDefinition, value) => {
  if (isEmptyValue(value)) {
    return [];
  }
  if (isMultienum(propertyDefinition)) {
    return parseMultiEnumValue(value);
  }
  return [value];
};
const getValidationError = ({
  originalPropertyDefinition,
  propertyDefinitionForActiveRules,
  value
}) => {
  if (isEmptyValue(value)) {
    return false;
  }
  const dependentFieldValues = getParsedPropertyValueAsArray(originalPropertyDefinition, value);
  const originalOptionValues = originalPropertyDefinition.options.map(option => option.value);
  const filteredOptionValues = propertyDefinitionForActiveRules.options.map(option => option.value);
  for (const dependentFieldValue of dependentFieldValues) {
    if (originalOptionValues.includes(dependentFieldValue) && !filteredOptionValues.includes(dependentFieldValue)) {
      return true;
    }
  }
  return false;
};
const getValidationMessage = ({
  originalPropertyDefinition,
  error,
  controllingFieldLabel
}) => {
  if (!error) {
    return null;
  }
  if (isMultienum(originalPropertyDefinition)) {
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: 'conditionalPropertyOptions.propertyInputV2.errors.hiddenOptionSelectedMulti',
      options: {
        controllingFieldLabel
      }
    });
  }
  return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: 'conditionalPropertyOptions.propertyInputV2.errors.hiddenOptionSelected',
    options: {
      controllingFieldLabel
    }
  });
};

// this error validation logic does not work for properties with external options
// As of Sep 2023, CPO tab is hidden for properties with external options. So, they cannot be set as dependent properties.
// Those properties are also disabled in controlling property selection. So, they cannot be set as controlling property.
export const getValidation = ({
  originalPropertyDefinition,
  propertyDefinitionForActiveRules,
  value,
  controllingFieldLabel
}) => {
  const error = getValidationError({
    originalPropertyDefinition,
    propertyDefinitionForActiveRules,
    value
  });
  return {
    error,
    validationMessage: getValidationMessage({
      originalPropertyDefinition,
      error,
      controllingFieldLabel
    })
  };
};
export const isEligibleForConditionalPropertyOptions = ({
  enableConditionalPropertyOptions,
  property,
  objectTypeId
}) => {
  if (!enableConditionalPropertyOptions) {
    return false;
  }
  const pipelineStagePropertyName = getPipelineStagePropertyName(objectTypeId);

  // For pipeline stage properties (deal stage, ticket stage, etc.), users can change stage using drag and drop on the board.
  // As of Feb 2024, CPO rules are not enforced on the board (columns are not disabled),
  // so edit modal does not allow setting up CPO rules for pipeline stage properties.
  // Before enabling CPO for pipeline stage properties, please work with the #crm-pipelines team.
  // CPO could contradict governance rules like "don't allow skipping stages".
  const isPipelineStageProperty = property.name === pipelineStagePropertyName;
  if (isPipelineStageProperty) {
    return false;
  }
  if (!isEnum(property)) {
    return false;
  }
  if (isCustomExternalOptionsProperty(property, objectTypeId) || isExternalOptionsProperty(property, objectTypeId)) {
    return false;
  }
  return true;
};

// finds out if a dependent property's value is valid given the related dependent property rules
// reuses the methods we use today in PropertyInputV2 as the logic needs to be same
export const isDependentPropertyValid = ({
  dependentProperty,
  dependentValue,
  conditionalPropertyOptionsRules
}) => {
  const values = getParsedPropertyValueAsArray(dependentProperty, dependentValue);

  // Filter out rules that are not related to the dependent property
  const dependentPropertyConditionalPropertyOptionsRules = conditionalPropertyOptionsRules.filter(rules => rules.dependentFieldName === dependentProperty.name);
  if (values.length === 0) {
    return true;
  }

  // An input is considered invalid only if it's value is present in original property definition but not in filtered definition
  // So, we need to build dependent property's definition with filtered options.
  // We are only building a new definition here, not passing it back to callers (properties form / property input)
  const dependentPropertyDefinitionWithFilteredOptions = buildPropertyDefinitionWithFilteredOptions(dependentProperty, dependentPropertyConditionalPropertyOptionsRules);
  return !getValidationError({
    originalPropertyDefinition: dependentProperty,
    propertyDefinitionForActiveRules: dependentPropertyDefinitionWithFilteredOptions,
    value: dependentValue
  });
};
export const getIsDependentCpoProperty = (rules, property) => {
  return rules.some(rule => rule.dependentFieldName === property.name);
};